import { useEffect } from "react";
import type { ReactNode } from "react";
import { Field, updateSyncWarnings } from "redux-form/immutable";
import type {
  BaseFieldProps,
  WrappedFieldInputProps,
  WrappedFieldMetaProps,
  WrappedFieldProps
} from "redux-form";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import AceEditor from "react-ace";
import type { IAceEditorProps } from "react-ace";

import FieldsHOC from "../fields-hoc";
import type { FieldHocProps } from "../fields-hoc";

import "ace-builds/webpack-resolver";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-css";
import "ace-builds/src-noconflict/mode-javascript";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/mode-typescript";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/mode-less";

const Label = styled.div`
  margin-left: 3px;
  margin-bottom: 3px;
  font-weight: 500;
`;

const Wrapper = styled.div<{ height: string }>`
  display: flex;
  width: 100%;
  align-items: center;
  height: ${(props) => props.height};
  @media only screen and (max-width: 680px) {
    flex-direction: column;
    align-items: stretch;
  }
  @media only screen and (max-width: 550px) {
    flex-direction: row;
    margin-left: 0px;
    align-items: center;
  }
  @media only screen and (max-width: 470px) {
    flex-direction: column;
    align-items: stretch;
    width: auto;
  }

  & > .ace_editor {
    z-index: 0;
  }
`;

const Brackets = styled.div`
  flex: 1 0 auto;
  font-size: 23px;
  margin-right: 5px;
`;

type AceAnnotations = Parameters<
  NonNullable<IAceEditorProps["onValidate"]>
>[0];

type ValidationOptions = Partial<Record<"dom" | "editor", boolean>>;

export type CodeEditorFieldProps = Pick<
  IAceEditorProps,
  "height" | "mode" | "width"
> & {
  givenSignature?: string;
  validateCustom?: ValidationOptions;
};

type CodeEditorInput = Pick<
  WrappedFieldInputProps,
  "name" | "onChange"
> & {
  value: string;
};

type CodeEditorMeta = FieldHocProps["meta"] &
  Pick<WrappedFieldMetaProps, "form">;

export type CodeEditorProps = CodeEditorFieldProps & {
  input: CodeEditorInput;
  meta: CodeEditorMeta;
};

const Editor = ({
  input,
  meta,
  mode = "typescript",
  width,
  height,
  givenSignature = undefined,
  validateCustom = {}
}: CodeEditorProps) => {
  const dispatch = useDispatch();

  const dispatchUpdate = (error = ""): void => {
    dispatch(
      updateSyncWarnings(
        meta.form,
        {
          [input.name]: error || undefined
        },
        undefined
      )
    );
  };

  useEffect(
    () => () => {
      if (Object.values(validateCustom).some((item) => Boolean(item))) {
        dispatchUpdate();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onValidate = (
    value: string,
    annotations: AceAnnotations
  ): void => {
    const { editor = false, dom = false } = validateCustom;
    let error: string | undefined;

    if (dom) {
      error = /(window|document)/.test(value)
        ? "syntaxDOMError"
        : error;
    }

    if (editor) {
      error = annotations.some((annotation) => annotation?.type === "error")
        ? "syntaxError"
        : error;
    }

    dispatchUpdate(error);
  };

  function getValue(
    value: string,
    action: "onChange" | "onInit"
  ): string;
  function getValue(value: string, action: string): string | null;
  function getValue(value: string, action: string): string | null {
    switch (action) {
      case "onChange":
        return givenSignature
          ? `${givenSignature}${value}}`
          : value;
      case "onInit":
        return givenSignature
          ? value.replace(givenSignature, "").slice(0, -1)
          : value;
      default:
        return null;
    }
  }

  const onChange: NonNullable<IAceEditorProps["onChange"]> = (
    newValue
  ) => {
    input.onChange(getValue(newValue, "onChange"));
  };

  return (
    <Wrapper height={height || "auto"}>
      {givenSignature && <Brackets>{givenSignature}</Brackets>}
      <AceEditor
        mode={mode}
        theme="monokai"
        value={getValue(input.value, "onInit")}
        name={input.name}
        onChange={onChange}
        onValidate={(annotations) =>
          onValidate(input.value, annotations)
        }
        width={width || "100%"}
        height={height || "150px"}
        showGutter
        editorProps={{
          $blockScrolling: Infinity
        }}
      />
      {givenSignature && <Brackets>{"}"}</Brackets>}
    </Wrapper>
  );
};

export type UpCodeEditorProps = CodeEditorFieldProps & {
  label?: ReactNode;
  name: string;
};

type ReduxFormEditorProps = WrappedFieldProps &
  CodeEditorFieldProps;

const ReduxFormEditor = ({
  input,
  meta,
  ...props
}: ReduxFormEditorProps) => {
  if (typeof input.value !== "string") {
    throw new TypeError("UpCodeEditor expects a string field value");
  }

  return (
    <Editor
      {...props}
      input={{
        name: input.name,
        value: input.value,
        onChange: (value) => input.onChange(value)
      }}
      meta={{
        form: meta.form,
        error: meta.error,
        submitFailed: meta.submitFailed
      }}
    />
  );
};

const UpCodeEditor = ({
  name,
  label,
  ...props
}: UpCodeEditorProps) => (
  <>
    {label && <Label>{label}</Label>}
    <Field<BaseFieldProps<CodeEditorFieldProps> & CodeEditorFieldProps>
      name={name}
      component={ReduxFormEditor}
      {...props}
    />
  </>
);

export default UpCodeEditor;

export const renderEditor = FieldsHOC(Editor);
